import { useEffect, useRef, useState } from "react";
import { GO_KEYS, isTyping, singleKeysEnabled } from "./shortcuts";

interface Actions {
  openPalette: () => void;
  openShortcuts: () => void;
  toggleTheme: () => void;
  go: (to: string) => void;
}

/** How long "G" waits for the letter of where to go. */
const GO_WINDOW_MS = 1500;

/**
 * The shortcuts that work anywhere in the app (see shortcuts.ts). They listen before the screen
 * does, so a key they use ("?", or the letter after "G") never also reaches a screen's own
 * shortcut. Character keys stay quiet while you type, while a dialog is open, and when
 * single-key shortcuts are turned off. Returns whether "G" is waiting for its letter.
 */
export function useGlobalShortcuts(actions: Actions): boolean {
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });
  const [going, setGoing] = useState(false);

  useEffect(() => {
    let pending = false;
    let timer = 0;
    const stop = () => {
      if (!pending) return;
      pending = false;
      window.clearTimeout(timer);
      setGoing(false);
    };
    const take = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onKey = (e: KeyboardEvent) => {
      const a = latest.current;
      // Ctrl K works everywhere, even while typing.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        take(e);
        stop();
        if (!document.querySelector('[aria-modal="true"]')) a.openPalette();
        return;
      }
      if (e.repeat || e.isComposing) return;
      if (e.ctrlKey || e.metaKey || e.altKey) {
        stop();
        return;
      }
      if (isTyping(e.target) || document.querySelector('[aria-modal="true"]')) {
        stop();
        return;
      }
      if (!singleKeysEnabled()) return;

      if (pending) {
        if (e.key === "Shift") return;
        const where = e.shiftKey ? undefined : GO_KEYS.find((g) => g.key === e.key.toLowerCase());
        stop();
        if (where) {
          take(e);
          a.go(where.to);
        }
        return;
      }
      if (e.key === "?") {
        take(e);
        a.openShortcuts();
      } else if (e.key === "/") {
        take(e);
        a.openPalette();
      } else if (e.key === "t" && !e.shiftKey) {
        take(e);
        a.toggleTheme();
      } else if (e.key === "g" && !e.shiftKey) {
        take(e);
        pending = true;
        setGoing(true);
        timer = window.setTimeout(stop, GO_WINDOW_MS);
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      window.clearTimeout(timer);
    };
  }, []);

  return going;
}
